import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Grid,
  GridItem,
  Text,
  Alert,
  AlertIcon,
  VStack,
  HStack,
  Badge,
} from '@chakra-ui/react';
import { useJsApiLoader } from '@react-google-maps/api';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

export interface ValidatedAddress {
  street_number: string;
  street_name: string;
  city: string;
  province: string;
  postal_code: string;
}

interface ValidateAddressModalProps {
  isOpen: boolean;
  onClose: () => void;
  onValidate: (address: ValidatedAddress) => void;
  initialAddress: string;
  initialCity: string;
  initialProvince: string;
  initialPostalCode: string;
  savedValidation?: ValidatedAddress | null;
  isAddressValidated?: boolean;
}

const isValidationComplete = (address: ValidatedAddress | null): boolean => {
  if (!address) return false;
  return !!(
    address.street_number.trim() &&
    address.street_name.trim() &&
    address.postal_code.trim()
  );
};

const getMissingFields = (address: ValidatedAddress): string[] => {
  const missing: string[] = [];
  if (!address.street_number.trim()) missing.push('Street Number');
  if (!address.street_name.trim()) missing.push('Street Name');
  if (!address.postal_code.trim()) missing.push('Postal Code');
  return missing;
};

const ValidateAddressModal = ({
  isOpen,
  onClose,
  onValidate,
  initialAddress,
  initialCity,
  initialProvince,
  initialPostalCode,
  savedValidation,
  isAddressValidated,
}: ValidateAddressModalProps) => {
  const [searchAddress, setSearchAddress] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validatedAddress, setValidatedAddress] =
    useState<ValidatedAddress | null>(null);

  const geocoderRef = useRef<google.maps.Geocoder | null>(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  useEffect(() => {
    if (isLoaded && !geocoderRef.current) {
      geocoderRef.current = new google.maps.Geocoder();
    }
  }, [isLoaded]);

  useEffect(() => {
    if (isOpen) {
      const fullAddress = [
        initialAddress,
        initialCity,
        initialProvince,
        initialPostalCode,
      ]
        .filter(Boolean)
        .join(', ');
      setSearchAddress(fullAddress);
      setError(null);

      if (isAddressValidated && savedValidation) {
        setValidatedAddress(savedValidation);
      } else {
        setValidatedAddress(null);
      }
    }
  }, [
    isOpen,
    initialAddress,
    initialCity,
    initialProvince,
    initialPostalCode,
    savedValidation,
    isAddressValidated,
  ]);

  const handleSearch = useCallback(async () => {
    if (!searchAddress.trim()) {
      setError('Please enter an address to search');
      return;
    }

    if (!geocoderRef.current) {
      setError('Maps not ready. Please try again.');
      return;
    }

    setIsValidating(true);
    setError(null);
    setValidatedAddress(null);

    const fullAddress = searchAddress.includes('Canada')
      ? searchAddress
      : `${searchAddress}, Canada`;

    geocoderRef.current.geocode({ address: fullAddress }, (results, status) => {
      setIsValidating(false);

      if (status === 'OK' && results && results.length > 0) {
        const result = results[0];
        const components = result.address_components;

        let streetNumber = '';
        let streetName = '';
        let city = '';
        let province = '';
        let postalCode = '';

        for (const component of components) {
          const types = component.types;
          if (types.includes('street_number')) {
            streetNumber = component.long_name;
          } else if (types.includes('route')) {
            streetName = component.long_name;
          } else if (types.includes('locality')) {
            city = component.long_name;
          } else if (types.includes('administrative_area_level_1')) {
            province = component.short_name;
          } else if (types.includes('postal_code')) {
            postalCode = component.long_name;
          }
        }

        const validated = {
          street_number: streetNumber,
          street_name: streetName,
          city,
          province,
          postal_code: postalCode,
        };

        setValidatedAddress(validated);

        if (!isValidationComplete(validated)) {
          const missing = getMissingFields(validated);
          setError(
            `Address found but missing required fields: ${missing.join(', ')}. Please enter a more specific address.`,
          );
        }
      } else {
        setError('Address not found. Please check and try again.');
      }
    });
  }, [searchAddress]);

  const handleConfirm = () => {
    if (validatedAddress && isValidationComplete(validatedAddress)) {
      onValidate(validatedAddress);
      onClose();
    }
  };

  const handleClose = () => {
    setSearchAddress('');
    setValidatedAddress(null);
    setError(null);
    onClose();
  };

  const canConfirm = validatedAddress && isValidationComplete(validatedAddress);
  const hasIncompleteValidation =
    validatedAddress && !isValidationComplete(validatedAddress);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Validate Address</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel fontSize="sm">Search Address</FormLabel>
              <Input
                variant="main"
                size="sm"
                value={searchAddress}
                onChange={(e) => setSearchAddress(e.target.value)}
                placeholder="Enter full address to search..."
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </FormControl>

            <Button
              colorScheme="brand"
              size="sm"
              onClick={handleSearch}
              isLoading={isValidating}
              isDisabled={!isLoaded || !searchAddress.trim()}
            >
              Search
            </Button>

            {error && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">{error}</Text>
              </Alert>
            )}

            {validatedAddress && (
              <Alert
                status={hasIncompleteValidation ? 'warning' : 'success'}
                borderRadius="md"
              >
                <AlertIcon />
                <VStack align="start" spacing={1} flex={1}>
                  <HStack>
                    <Text fontWeight="bold" fontSize="sm">
                      {hasIncompleteValidation
                        ? 'Incomplete Address'
                        : 'Address Found'}
                    </Text>
                    {!hasIncompleteValidation && (
                      <Badge colorScheme="green">Validated</Badge>
                    )}
                  </HStack>
                  <Grid
                    templateColumns="repeat(2, 1fr)"
                    gap={2}
                    w="100%"
                    pt={2}
                  >
                    <GridItem>
                      <Text fontSize="xs" color="gray.500">
                        Street Number{' '}
                        {!validatedAddress.street_number && (
                          <Text as="span" color="red.500">
                            *
                          </Text>
                        )}
                      </Text>
                      <Text
                        fontSize="sm"
                        color={
                          validatedAddress.street_number ? 'inherit' : 'red.500'
                        }
                      >
                        {validatedAddress.street_number || 'Missing'}
                      </Text>
                    </GridItem>
                    <GridItem>
                      <Text fontSize="xs" color="gray.500">
                        Street Name{' '}
                        {!validatedAddress.street_name && (
                          <Text as="span" color="red.500">
                            *
                          </Text>
                        )}
                      </Text>
                      <Text
                        fontSize="sm"
                        color={
                          validatedAddress.street_name ? 'inherit' : 'red.500'
                        }
                      >
                        {validatedAddress.street_name || 'Missing'}
                      </Text>
                    </GridItem>
                    <GridItem>
                      <Text fontSize="xs" color="gray.500">
                        City
                      </Text>
                      <Text fontSize="sm">{validatedAddress.city || '-'}</Text>
                    </GridItem>
                    <GridItem>
                      <Text fontSize="xs" color="gray.500">
                        Province
                      </Text>
                      <Text fontSize="sm">
                        {validatedAddress.province || '-'}
                      </Text>
                    </GridItem>
                    <GridItem>
                      <Text fontSize="xs" color="gray.500">
                        Postal Code{' '}
                        {!validatedAddress.postal_code && (
                          <Text as="span" color="red.500">
                            *
                          </Text>
                        )}
                      </Text>
                      <Text
                        fontSize="sm"
                        color={
                          validatedAddress.postal_code ? 'inherit' : 'red.500'
                        }
                      >
                        {validatedAddress.postal_code || 'Missing'}
                      </Text>
                    </GridItem>
                  </Grid>
                </VStack>
              </Alert>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            colorScheme="brand"
            onClick={handleConfirm}
            isDisabled={!canConfirm}
          >
            Confirm
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ValidateAddressModal;
